'use client'

import React, { useState, useCallback, useMemo, useRef, useEffect, useImperativeHandle, forwardRef } from 'react'
import Map, { Marker, Popup, NavigationControl } from 'react-map-gl/maplibre'
import type { MapRef } from 'react-map-gl/maplibre'
import Supercluster from 'supercluster'
import 'maplibre-gl/dist/maplibre-gl.css'

const MAP_STYLE = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json'

export type LocationPin = {
  name: string
  latitude: number
  longitude: number
  type: 'office' | 'region' | 'combined'
}

type PointProperties = {
  name: string
  type: 'office' | 'region' | 'combined'
}

type InteractiveMapProps = {
  pins: LocationPin[]
  activeLocationKey?: string | null
  onReset?: () => void
  className?: string
}

export type InteractiveMapHandle = {
  flyToLocation: (longitude: number, latitude: number) => void
}

const PIN_PATH = 'M10.0811 0.5271C15.6488 0.5271 20.163 5.04046 20.1631 10.6082C20.1631 18.0435 12.3539 22.4315 10.4814 23.3767C10.2272 23.5051 9.93593 23.5051 9.68164 23.3767C7.80969 22.4318 0 18.0438 0 10.6082C9.88337e-05 5.04056 4.51347 0.527264 10.0811 0.5271ZM10.0811 6.28784C7.6951 6.28801 5.76091 8.2222 5.76074 10.6082C5.76074 12.9942 7.695 14.9293 10.0811 14.9294C12.4672 14.9294 14.4023 12.9943 14.4023 10.6082C14.4022 8.2221 12.4671 6.28784 10.0811 6.28784Z'

export const InteractiveMap = forwardRef<InteractiveMapHandle, InteractiveMapProps>(({
  pins,
  activeLocationKey,
  onReset,
  className,
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MapRef>(null)
  const [hoveredPin, setHoveredPin] = useState<LocationPin | null>(null)
  const [hoveredCluster, setHoveredCluster] = useState<{
    longitude: number
    latitude: number
    pins: PointProperties[]
  } | null>(null)
  const [zoom, setZoom] = useState(1)
  const [bounds, setBounds] = useState<[number, number, number, number] | null>(null)
  const [mapReady, setMapReady] = useState(false)
  const [showReset, setShowReset] = useState(false)
  const flyingRef = useRef(false)
  const hasFlewRef = useRef(false)

  const resetView = useCallback(() => {
    if (pins.length === 0) return
    const map = mapRef.current
    if (!map) return
    const lngs = pins.map((p) => p.longitude)
    const lats = pins.map((p) => p.latitude)
    const sw: [number, number] = [Math.min(...lngs), Math.min(...lats)]
    const ne: [number, number] = [Math.max(...lngs), Math.max(...lats)]
    setHoveredCluster(null)
    setHoveredPin(null)
    setShowReset(false)
    onReset?.()
    map.fitBounds([sw, ne], { padding: 60, duration: 1500, maxZoom: 5 })
  }, [pins, onReset])

  useImperativeHandle(ref, () => ({
    flyToLocation: (longitude: number, latitude: number) => {
      setHoveredCluster(null)
      setHoveredPin(null)
      setShowReset(true)
      mapRef.current?.flyTo({ center: [longitude, latitude], zoom: 5, duration: 1500 })
    },
  }))

  const points: Supercluster.PointFeature<PointProperties>[] = useMemo(
    () =>
      pins.map((pin, index) => ({
        type: 'Feature' as const,
        properties: { name: pin.name, type: pin.type },
        geometry: {
          type: 'Point' as const,
          coordinates: [pin.longitude, pin.latitude],
        },
        id: index,
      })),
    [pins],
  )

  const cluster = useMemo(() => {
    const sc = new Supercluster<PointProperties>({ radius: 50, maxZoom: 6 })
    sc.load(points)
    return sc
  }, [points])

  const clusters = useMemo(() => {
    if (!bounds) return []
    return cluster.getClusters(bounds, Math.floor(zoom))
  }, [cluster, bounds, zoom])

  const handleMove = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const b = map.getBounds()
    setBounds([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()])
    setZoom(map.getZoom())
  }, [])

  const handleLoad = useCallback(() => {
    handleMove()
    setMapReady(true)
  }, [handleMove])

  const flyToMarkers = useCallback(() => {
    if (hasFlewRef.current || !mapReady || pins.length === 0) return
    const map = mapRef.current
    if (!map) return

    hasFlewRef.current = true

    const lngs = pins.map((p) => p.longitude)
    const lats = pins.map((p) => p.latitude)
    const sw: [number, number] = [Math.min(...lngs), Math.min(...lats)]
    const ne: [number, number] = [Math.max(...lngs), Math.max(...lats)]

    setTimeout(() => {
      flyingRef.current = true
      map.fitBounds([sw, ne], { padding: 60, duration: 2000, maxZoom: 5 })
      setTimeout(() => { flyingRef.current = false }, 2100)
    }, 300)
  }, [mapReady, pins])

  // Trigger fly-to when map scrolls into view
  useEffect(() => {
    const el = containerRef.current
    if (!el || !mapReady) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          flyToMarkers()
          observer.disconnect()
        }
      },
      { threshold: 0.3 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [mapReady, flyToMarkers])

  const handleInteraction = useCallback(() => {
    // Ignore programmatic moves (fly-to on load)
    if (flyingRef.current) return
    setHoveredCluster(null)
    setShowReset(true)
  }, [])

  const handleClusterClick = useCallback(
    (clusterId: number, longitude: number, latitude: number) => {
      setHoveredCluster(null)
      const expansionZoom = Math.min(cluster.getClusterExpansionZoom(clusterId), 16)
      mapRef.current?.flyTo({ center: [longitude, latitude], zoom: expansionZoom, duration: 500 })
    },
    [cluster],
  )

  const handleClusterEnter = useCallback(
    (clusterId: number, longitude: number, latitude: number) => {
      const leaves = cluster.getLeaves(clusterId, Infinity)
      setHoveredCluster({
        longitude,
        latitude,
        pins: leaves.map((l) => l.properties),
      })
    },
    [cluster],
  )

  const handleClusterLeave = useCallback(() => {
    setHoveredCluster(null)
  }, [])

  const handleMarkerEnter = useCallback((pin: LocationPin) => {
    setHoveredPin(pin)
  }, [])

  const handleMarkerLeave = useCallback(() => {
    setHoveredPin(null)
  }, [])

  // For combined markers, match if either office or region key matches
  const isMarkerActive = (pin: PointProperties) => {
    if (!activeLocationKey) return false
    if (pin.type === 'combined') {
      return activeLocationKey === `office-${pin.name}` || activeLocationKey === `region-${pin.name}`
    }
    return activeLocationKey === `${pin.type}-${pin.name}`
  }

  const hasPulse = (type: string) => type === 'region' || type === 'combined'

  return (
    <div
      ref={containerRef}
      className={`relative ${className ?? ''}`}
      role="img"
      aria-label="World map showing office and data centre region locations"
    >
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: 15,
          latitude: 25,
          zoom: 0.5,
        }}
        minZoom={0.5}
        maxZoom={5}
        renderWorldCopies={false}
        onMove={handleMove}
        onLoad={handleLoad}
        onDragStart={handleInteraction}
        onZoomStart={handleInteraction}
        style={{ width: '100%', height: '100%', minHeight: 300 }}
        mapStyle={MAP_STYLE}
        attributionControl={false}
      >
        <NavigationControl position="top-right" showCompass={false} />

        {clusters.map((feature) => {
          const [longitude, latitude] = feature.geometry.coordinates
          const props = feature.properties as Record<string, unknown>
          const isCluster = props.cluster

          if (isCluster) {
            const pointCount = props.point_count as number
            const size = 24 + Math.min(pointCount * 4, 16)

            return (
              <Marker
                key={`cluster-${feature.id}`}
                longitude={longitude}
                latitude={latitude}
                anchor="center"
              >
                <div
                  className="flex cursor-pointer items-center justify-center rounded-full bg-civo-lime text-black shadow-md transition-transform duration-150 hover:scale-110"
                  style={{ width: size, height: size }}
                  onClick={() => handleClusterClick(feature.id as number, longitude, latitude)}
                  onMouseEnter={() => handleClusterEnter(feature.id as number, longitude, latitude)}
                  onMouseLeave={handleClusterLeave}
                >
                  <span className="text-xs font-semibold">{pointCount}</span>
                </div>
              </Marker>
            )
          }

          const pin = feature.properties as PointProperties
          const isActive = isMarkerActive(pin)

          return (
            <Marker
              key={`${pin.type}-${feature.id}`}
              longitude={longitude}
              latitude={latitude}
              anchor="bottom"
            >
              <div
                className={`cursor-pointer transition-transform duration-300 ease-in-out hover:scale-125 ${isActive ? 'scale-150 z-10' : ''}`}
                style={{ transformOrigin: 'bottom center' }}
                onMouseEnter={() =>
                  handleMarkerEnter({ name: pin.name, type: pin.type, latitude, longitude })
                }
                onMouseLeave={handleMarkerLeave}
                role="img"
                aria-label={`${pin.name} (${pin.type === 'combined' ? 'Office & Region' : pin.type === 'office' ? 'Office' : 'Region'})`}
              >
                <svg width="24" height="28" viewBox="0 0 21 24" fill="none" className="overflow-visible drop-shadow-md">
                  {hasPulse(pin.type) && (
                    <>
                      <circle cx="10.08" cy="10.6" r="8" fill="#00FF6F" opacity="0">
                        <animate attributeName="r" from="8" to="18" dur="2s" repeatCount="indefinite" />
                        <animate attributeName="opacity" from="0.4" to="0" dur="2s" repeatCount="indefinite" />
                      </circle>
                      <circle cx="10.08" cy="10.6" r="8" fill="#00FF6F" opacity="0">
                        <animate attributeName="r" from="8" to="18" dur="2s" begin="1s" repeatCount="indefinite" />
                        <animate attributeName="opacity" from="0.4" to="0" dur="2s" begin="1s" repeatCount="indefinite" />
                      </circle>
                    </>
                  )}
                  {pin.type === 'combined' ? (
                    <>
                      <path d={PIN_PATH} fill="black" />
                      <path d={PIN_PATH} fill="#00FF6F" style={{ clipPath: 'polygon(50% 0%, 100% 0%, 100% 100%, 50% 100%)' }} />
                      <path d={PIN_PATH} fill="none" stroke="white" strokeWidth={1.5} />
                    </>
                  ) : (
                    <path
                      d={PIN_PATH}
                      fill={pin.type === 'office' ? 'black' : '#00FF6F'}
                      stroke={pin.type === 'region' ? 'white' : 'none'}
                      strokeWidth={pin.type === 'region' ? 1.5 : 0}
                    />
                  )}
                </svg>
              </div>
            </Marker>
          )
        })}

        {hoveredPin && (
          <Popup
            longitude={hoveredPin.longitude}
            latitude={hoveredPin.latitude}
            anchor="bottom"
            offset={12}
            closeButton={false}
            closeOnClick={false}
          >
            {hoveredPin.type === 'combined' ? (
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1.5">
                  <span className="inline-block size-2 rounded-full" style={{ backgroundColor: '#000000' }} />
                  <span className="body-small text-black">{hoveredPin.name}</span>
                  <span className="body-small text-black/50">Office</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-block size-2 rounded-full" style={{ backgroundColor: '#00FF6F' }} />
                  <span className="body-small text-black">{hoveredPin.name}</span>
                  <span className="body-small text-black/50">Region</span>
                </div>
              </div>
            ) : (
              <>
                <span className="body-small text-black">{hoveredPin.name}</span>
                <span className="body-small ml-1.5 text-black/50">
                  {hoveredPin.type === 'office' ? 'Office' : 'Region'}
                </span>
              </>
            )}
          </Popup>
        )}

        {hoveredCluster && (
          <Popup
            longitude={hoveredCluster.longitude}
            latitude={hoveredCluster.latitude}
            anchor="bottom"
            offset={20}
            closeButton={false}
            closeOnClick={false}
          >
            <div className="flex flex-col gap-0.5">
              {hoveredCluster.pins.map((pin, i) => (
                pin.type === 'combined' ? (
                  <React.Fragment key={i}>
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block size-2 rounded-full" style={{ backgroundColor: '#000000' }} />
                      <span className="body-small text-black">{pin.name}</span>
                      <span className="body-small text-black/50">Office</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block size-2 rounded-full" style={{ backgroundColor: '#00FF6F' }} />
                      <span className="body-small text-black">{pin.name}</span>
                      <span className="body-small text-black/50">Region</span>
                    </div>
                  </React.Fragment>
                ) : (
                  <div key={i} className="flex items-center gap-1.5">
                    <span
                      className="inline-block size-2 rounded-full"
                      style={{ backgroundColor: pin.type === 'office' ? '#000000' : '#00FF6F' }}
                    />
                    <span className="body-small text-black">{pin.name}</span>
                    <span className="body-small text-black/50">
                      {pin.type === 'office' ? 'Office' : 'Region'}
                    </span>
                  </div>
                )
              ))}
            </div>
          </Popup>
        )}
      </Map>

      {/* Reset view button */}
      <button
        className={`absolute left-2 top-2 z-10 flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 shadow-md transition-all duration-300 ease-in-out cursor-pointer hover:bg-gray-50 ${showReset ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}
        onClick={resetView}
        aria-label="Show all locations"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1 5L7 1L13 5" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3 7V12H11V7" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-xs font-medium text-black">Show all</span>
      </button>

      {/* Explore hint */}
      <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2">
        <div className="flex items-center gap-1.5 rounded-full bg-civo-black/70 px-3 py-1.5 backdrop-blur-sm">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="animate-bounce">
            <path d="M8 2v8M5 7l3 3 3-3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="8" cy="13" r="1" fill="white" />
          </svg>
          <span className="text-xs text-white">Scroll to explore</span>
        </div>
      </div>
    </div>
  )
})

InteractiveMap.displayName = 'InteractiveMap'
